import React, { Dispatch, FC, SetStateAction, useCallback, useState } from 'react';
import { Button, Icons, Search } from '@silverfort/sf-components';
import { UsersQueryBuilder } from './UsersQueryBuilder';
import { UserFilters } from '../../../types/UserFilters';
import { SortDirection } from '@silverfort/sf-components/dist/Table/interface';
import { User } from '../../../types/User';
import { UsersTable } from './UsersTable';
import { UsersViewRenderMode } from './UsersViewRenderMode';
import { Sorting } from '../../../types/Sorting';

type UsersViewProps = {
    downloadReport: () => Promise<void>;
    downloading: boolean;
    fetchMore: () => Promise<void>;
    loading: boolean;
    renderMode: UsersViewRenderMode;
    setFilters: Dispatch<SetStateAction<UserFilters>>;
    setSorting: Dispatch<SetStateAction<Sorting>>;
    users: Array<User>;
};

export const UsersView: FC<UsersViewProps> = ({
    downloadReport,
    downloading,
    fetchMore,
    loading,
    renderMode,
    setFilters,
    setSorting,
    users
}) => {
    const [searchText, setSearchText] = useState('');

    const updateFilter = useCallback(<K extends keyof UserFilters>(field: K, value: UserFilters[K]) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    }, [setFilters]);

    const onSearch = useCallback(
        (text?: string) => {
            setSearchText(text ?? '');

            updateFilter('name', text ?? '');
        },
        [updateFilter]
    );

    const onSort = useCallback(
        (field: keyof User, direction: SortDirection) => {
            if (!users.length) return undefined;

            setSorting({ direction, field });
        },
        [setSorting, users]
    );

    return (
        <section className="flex flex-col gap-4 h-full">
            <div className="flex justify-between">
                <UsersQueryBuilder updateFilter={updateFilter} />

                <div className="flex gap-2">
                    <Search border className="w-50" onChange={onSearch} placeholder="Search" text={searchText} />

                    <Button
                        disabled={!users.length}
                        iconRight={Icons.Download}
                        isLoading={downloading}
                        onClick={downloadReport}
                        title="Export Users"
                    />
                </div>
            </div>

            <UsersTable
                loading={loading}
                onScrollEnd={fetchMore}
                onSort={onSort}
                renderMode={renderMode}
                users={users}
            />
        </section>
    );
};
